import React, { useEffect, useState, useCallback } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { PaperPlaneTilt, FilePdf, Plus, CheckCircle, XCircle, MapPin, WhatsappLogo, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PROVINCE_NAMES, provinceColor } from "../lib/provinces";
import { remittanceWaLink } from "./RemittancesPanel";
import { Checkbox } from "./ui/checkbox";

const money = (v, cur) => `${cur === "USD" ? "US$" : "RD$"} ${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ClientRemittances = ({ savingsAccts = [] }) => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [feeDop, setFeeDop] = useState(0);
  const [feeUsd, setFeeUsd] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [freeSend, setFreeSend] = useState(false);
  const [deliveryAmount, setDeliveryAmount] = useState("");
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("savings");
  const [accId, setAccId] = useState("");
  const [cashCurrency, setCashCurrency] = useState("DOP");
  const [amount, setAmount] = useState("");
  const [rName, setRName] = useState("");
  const [rId, setRId] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    api.get("/me/remittances").then((r) => setRows(r.data)).catch(() => {});
    api.get("/me/remittance-fee").then((r) => { setFeeDop(r.data.fee || 0); setFeeUsd(r.data.fee_usd || 0); setEnabled(r.data.enabled !== false); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectedAcc = savingsAccts.find((a) => a.id === accId);
  const cur = method === "savings" && selectedAcc ? selectedAcc.currency : cashCurrency;
  const fee = freeSend ? 0 : (cur === "USD" ? feeUsd : feeDop);
  const amt = Number(amount) || 0;
  const total = amt + Number(fee || 0);

  const submit = async () => {
    if (!rName.trim()) { toast.error(t("rm_need_recipient")); return; }
    if (!province) { toast.error(t("rm_need_province")); return; }
    if (amt <= 0) { toast.error(t("rm_need_amount")); return; }
    if (method === "savings" && !accId) { toast.error(t("rm_need_account")); return; }
    if (freeSend && !(Number(deliveryAmount) > 0)) { toast.error(t("rm_need_delivery")); return; }
    setBusy(true);
    try {
      await api.post("/me/remittances", {
        payment_method: method, from_account_id: method === "savings" ? accId : null,
        amount: amt, currency: cur, free_send: freeSend, delivery_amount: freeSend ? Number(deliveryAmount) : null,
        recipient_name: rName, recipient_id: rId, recipient_phone: rPhone,
        province, city, note,
      });
      toast.success(t("rm_created"));
      setOpen(false);
      setAccId(""); setAmount(""); setFreeSend(false); setDeliveryAmount(""); setRName(""); setRId(""); setRPhone(""); setProvince(""); setCity(""); setNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const downloadReceipt = async (r) => {
    try {
      const res = await api.get(`/me/remittances/${r.id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `envio_${r.reference}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("error")); }
  };

  return (
    <div className="mt-8" data-testid="client-remittances">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display font-bold text-emerald-900 text-xl">{t("remittances_title")}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {rows.length > 0 && (
            <div className="relative w-full sm:w-64">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="client-remittance-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("rm_client_search_ph")} className="h-11 pl-9" />
            </div>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="client-new-remittance-btn" disabled={!enabled} title={!enabled ? t("rm_disabled_msg") : ""} className="bg-emerald-800 hover:bg-emerald-900 gap-2 h-11"><Plus size={18} weight="bold" /> {t("rm_new")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{t("rm_new")}</DialogTitle><DialogDescription>{t("remittances_sub")}</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <label className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 cursor-pointer" data-testid="c-rm-free-label">
                <Checkbox data-testid="c-rm-free" checked={freeSend} onCheckedChange={(v) => setFreeSend(!!v)} />
                <span className="text-sm font-semibold text-amber-800">{t("rm_free_send")}</span>
                <span className="text-xs text-amber-600">— {t("rm_free_hint")}</span>
              </label>
              <div>
                <Label>{t("rm_payment_method")}</Label>
                <Select value={method} onValueChange={(v) => { setMethod(v); setAccId(""); }}>
                  <SelectTrigger data-testid="c-rm-method" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">{t("rm_from_savings")}</SelectItem>
                    <SelectItem value="cash">{t("rm_cash")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {method === "savings" && (
                <div>
                  <Label>{t("rm_account")}</Label>
                  <Select value={accId} onValueChange={setAccId}>
                    <SelectTrigger data-testid="c-rm-account" className="mt-1 h-11"><SelectValue placeholder={t("rm_select_account")} /></SelectTrigger>
                    <SelectContent>
                      {savingsAccts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.account_number} · {money(a.balance, a.currency)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {savingsAccts.length === 0 && <p className="text-xs text-amber-600 mt-1">{t("rm_no_savings")}</p>}
                </div>
              )}
              {method === "cash" && (
                <div>
                  <Label>{t("rm_currency")}</Label>
                  <Select value={cashCurrency} onValueChange={setCashCurrency}>
                    <SelectTrigger data-testid="c-rm-currency" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DOP">RD$ (DOP)</SelectItem>
                      <SelectItem value="USD">US$ (USD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("rm_amount")} ({cur === "USD" ? "US$" : "RD$"})</Label>
                  <Input data-testid="c-rm-amount" type="number" min="0" step="0.01" className="mt-1 h-11 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div>
                  <Label>{t("rm_fee")}</Label>
                  <Input readOnly value={money(fee, cur)} className="mt-1 h-11 font-mono-num bg-slate-50" />
                </div>
              </div>
              <div className="rounded-lg bg-emerald-50 px-4 py-2.5 flex justify-between items-center">
                <span className="text-sm text-emerald-700 font-semibold">{t("rm_total_charge")}</span>
                <span className="font-mono-num font-bold text-emerald-900" data-testid="c-rm-total">{money(total, cur)}</span>
              </div>
              {freeSend && (
                <div>
                  <Label>{t("rm_delivery_amount")} (RD$)</Label>
                  <Input data-testid="c-rm-delivery" type="number" min="0" step="0.01" className="mt-1 h-11 font-mono-num" value={deliveryAmount} onChange={(e) => setDeliveryAmount(e.target.value)} />
                </div>
              )}
              <div className="border-t border-gray-100 pt-3 space-y-3">
                <div><Label>{t("rm_recipient")}</Label><Input data-testid="c-rm-recipient-name" className="mt-1 h-11" value={rName} onChange={(e) => setRName(e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("rm_recipient_id")}</Label><Input data-testid="c-rm-recipient-id" className="mt-1 h-11" value={rId} onChange={(e) => setRId(e.target.value)} /></div>
                  <div><Label>{t("rm_recipient_phone")}</Label><Input data-testid="c-rm-recipient-phone" className="mt-1 h-11" value={rPhone} onChange={(e) => setRPhone(e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>{t("rm_province")}</Label>
                    <Select value={province} onValueChange={setProvince}>
                      <SelectTrigger data-testid="c-rm-province" className="mt-1 h-11"><SelectValue placeholder={t("rm_select_province")} /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {PROVINCE_NAMES.map((p) => (
                          <SelectItem key={p} value={p}><span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: provinceColor(p) }} /> {p}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("rm_city")}</Label><Input data-testid="c-rm-city" className="mt-1 h-11" value={city} onChange={(e) => setCity(e.target.value)} /></div>
                </div>
                <div><Label>{t("rm_note")}</Label><Textarea data-testid="c-rm-note" className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="c-rm-submit" onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? t("rm_sending") : t("rm_confirm")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center text-slate-400" data-testid="client-remittances-empty">{t("rm_empty")}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-gray-100">
                <th className="px-4 py-3 font-medium">{t("date")}</th>
                <th className="px-4 py-3 font-medium">{t("reference")}</th>
                <th className="px-4 py-3 font-medium">{t("rm_recipient")}</th>
                <th className="px-4 py-3 font-medium">{t("rm_province")}</th>
                <th className="px-4 py-3 font-medium text-right">{t("rm_amount")}</th>
                <th className="px-4 py-3 font-medium text-center">{t("status")}</th>
                <th className="px-4 py-3 font-medium text-right">{t("receipt")}</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const ql = search.trim().toLowerCase();
                const shown = ql ? rows.filter((r) => [r.reference, r.recipient_name, r.recipient_id, r.city, r.province].some((v) => String(v || "").toLowerCase().includes(ql))) : rows;
                if (shown.length === 0) {
                  return <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="client-remittances-no-match">{t("rm_empty")}</td></tr>;
                }
                return shown.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0" data-testid={`client-remittance-row-${r.id}`}>
                  <td className="px-4 py-3 text-slate-600">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-500">{r.reference}</td>
                  <td className="px-4 py-3 text-emerald-900 font-semibold">{r.recipient_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: provinceColor(r.province) }}><MapPin size={12} weight="fill" /> {r.province}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num font-bold text-emerald-800">{money(r.amount, r.currency)}<div className="text-[10px] text-slate-400 font-normal">+{money(r.fee, r.currency)} {t("rm_fee_lower")}</div></td>
                  <td className="px-4 py-3 text-center">
                    {r.status === "delivered" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle size={13} weight="fill" /> {t("rm_delivered")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600"><XCircle size={13} weight="fill" /> {t("rm_not_received")}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.recipient_phone ? (
                      <a data-testid={`client-remittance-wa-${r.id}`} href={remittanceWaLink(r)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md bg-[#25D366] px-2 py-1.5 text-white hover:bg-[#1eb85a] mr-1.5 align-middle"><WhatsappLogo size={15} weight="fill" /></a>
                    ) : null}
                    <Button data-testid={`client-remittance-receipt-${r.id}`} size="sm" variant="outline" onClick={() => downloadReceipt(r)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1"><FilePdf size={14} weight="bold" /> {t("receipt")}</Button>
                  </td>
                </tr>
              ));
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
